/**
 * Agrégats de projet et de programme calculés par la passerelle (lot U6b ; IA v4.1 § 4.2, § 4.2.1,
 * § 4.5, § 4.6 ; ARB-r2-05, ARB-r2-09, ARB-r2-15, ARB-r2-18).
 *
 * Module séparé de `api` (client.ts), comme `niveauxApi` : la surface caractérisée du client
 * n'est pas modifiée. L'OpenAPI déclare ces routes NON TYPÉES : les formes ci-dessous sont lues
 * dans `ProjectAggregatesController` et ses services (`SanteProjet`, `CycleProjet`,
 * `DemandService`, `PlanificationService`, `RegressionService`, `ProjectAggregatesService`).
 *
 * Les lectures rendent `null` quand la passerelle n'expose pas la route (404, 405, 501) : l'écran
 * retombe alors sur son calcul client et le DIT (« non calculé par la passerelle »).
 */
import { ApiError, request } from './http'

const ws = (id: string): string => `/workspaces/${encodeURIComponent(id)}`

/** Route absente de cette passerelle (déploiement antérieur aux agrégats) : `null`, pas une erreur. */
export async function ouAbsent<T>(appel: () => Promise<T>): Promise<T | null> {
  try {
    return await appel()
  } catch (e) {
    if (e instanceof ApiError && [404, 405, 501].includes(e.status)) return null
    throw e
  }
}

// ---------------------------------------------------------------------------------------------
// Santé v1.1 et cycle en 9 étapes
// ---------------------------------------------------------------------------------------------

export type CouleurServeur = 'green' | 'amber' | 'red' | 'not_applicable'
export type CleSignalServeur = 'quality' | 'delivery' | 'decisions' | 'compliance' | 'colony' | 'operations' | 'generability'

export interface SignalServeur {
  key: CleSignalServeur | string
  label: string
  color: CouleurServeur | string
  code: string
  message: string
  params?: Record<string, unknown>
  warning_only?: boolean
}

export interface SanteServeur {
  workspace_id: string
  formula_version: string
  color: CouleurServeur | string
  determining_signals: { key: string; code: string; message: string }[]
  unaccepted_regression: boolean
  signals: SignalServeur[]
  last_non_regression?: unknown
}

export type EtatEtapeServeur = 'a_venir' | 'en_cours' | 'faite' | 'rouverte' | 'non_activee' | string

export interface EtapeCycleServeur {
  step: number
  key: string
  label: string
  state: EtatEtapeServeur
  demands: number
  items: number
}

export interface CycleServeur {
  workspace_id: string
  steps: EtapeCycleServeur[]
  gauge: { step: number; demands: number }[]
  steps_in_progress: number[]
  demands: unknown[]
  demands_total: number
  items_without_demand: number
}

// ---------------------------------------------------------------------------------------------
// Demandes DEM-n
// ---------------------------------------------------------------------------------------------

export type EtatDemandeServeur = 'idee' | 'cadree' | 'specifiee' | 'en_construction' | 'a_voir' | 'livree' | 'abandonnee' | string

export interface DemandeServeur {
  id: number
  number: number
  reference: string
  title: string
  requester: string | null
  created_at: string
  age_s: number | null
  abandon_reason: string | null
  state: EtatDemandeServeur
  stage: number
  stage_key: string
  items_count: number
}

export interface DemandesServeur {
  workspace_id: string
  demands: DemandeServeur[]
  items_without_demand: number
  /** O50 : règle « Livrée » appliquée par la passerelle, en clair. Absente : ancienne passerelle. */
  delivery_rule?: string
}

/** O50 : un déploiement RÉUSSI d'une version qui contient un item de la demande. */
export interface LivraisonDemande {
  release_id: string
  version: string
  environment: string
  production: boolean
  production_declared: boolean
  recette_granted: boolean
  deployed_at: string | null
}

export interface FilDemandeServeur extends Omit<DemandeServeur, 'items_count' | 'age_s' | 'stage_key'> {
  description: string | null
  stored_state: string | null
  source_kind: string | null
  stage_state: string
  steps: { step: number; key: string; state: string }[]
  cadrage: 'accepted' | 'proposed' | 'none' | string
  specifications: number[]
  items: { id: number; kind: string; title: string; status: string; stage: number; stage_key: string }[]
  missions: { id: string; title: string | null; kind: string | null; status: string }[]
  versions: { id: string | number; version: string; status: string | null }[]
  links: { kind: string; ref: string; linked_by: string | null; linked_at: string | null }[]
  /** O9 : dernier job de cadrage de l'idée (état dérivé ; cause s'il a échoué). Absent : ancienne passerelle. */
  cadrage_job?: JobSuiteDemande | null
  /** O13 : approbation COURANTE du cadrage (version non remplacée). */
  cadrage_approval?: { id: number; status: 'pending' | 'approved' | 'rejected' | string; review_verdict: string | null; version_n: number | null } | null
  /** O13 : dernier job « spécification depuis ce cadrage ». */
  spec_draft?: JobSuiteDemande | null
  /** O50 : règle « Livrée » et déploiements qui la fondent. Absents : ancienne passerelle. */
  delivery_rule?: string
  deliveries?: LivraisonDemande[]
}

export interface JobSuiteDemande {
  job_id: string
  status: 'pending' | 'processing' | 'done' | 'failed' | 'worker_lost' | string
  error: string | null
  created_at: string | null
}

// ---------------------------------------------------------------------------------------------
// Planifier : limites de travail en cours et définition de prêt
// ---------------------------------------------------------------------------------------------

export interface LimitesServeur {
  workspace_id: string
  scope: 'active_sprint' | 'board' | string
  limits: Record<string, number>
  counts: Record<string, number>
  states: Record<string, 'sous_limite' | 'atteinte' | 'depassee' | string>
}

export interface PretItemServeur {
  id: number
  kind: 'feature' | 'story' | string
  title: string
  prete: boolean
  manques: string[]
  non_evalues: string[]
}

export interface PretBacklogServeur {
  workspace_id: string
  items: PretItemServeur[]
  ready_count: number
  total: number
}

// ---------------------------------------------------------------------------------------------
// Vérifier : régressions et leur acceptation (ARB-r2-18)
// ---------------------------------------------------------------------------------------------

export interface AcceptationRegression {
  accepted_by: string
  accepted_as: string
  deadline: string
  reason: string
  accepted_at: string
}

export interface RegressionsServeur {
  campaign_id: number | null
  baseline_campaign_id: number | null
  regressions: { test_case_id: number; external_key: string | null; name: string | null; accepted: boolean; acceptance: AcceptationRegression | null }[]
  unaccepted: number
  accepted: number
}

export interface AccepterRegressionEntree {
  campaign_id: number
  test_case_id: number
  reason: string
  /** ISO-8601 avec fuseau, dans le futur. */
  deadline: string
}

// ---------------------------------------------------------------------------------------------
// Programme : vue d'ensemble
// ---------------------------------------------------------------------------------------------

export interface ProjetVueProgramme {
  workspace_id: string
  name: string
  health: { color: CouleurServeur | string; formula_version: string; determining_signals: { key: string; code: string; message: string }[]; unaccepted_regression: boolean }
  stage: { steps_in_progress: number[]; gauge: { step: number; demands: number }[] }
  stack: string | null
}

export interface VueProgrammeServeur {
  program_id: string
  name: string
  status: string | null
  objective: string | null
  next_milestone: Record<string, unknown> | null
  health_distribution: Record<string, number>
  worst_health: string | null
  projects: ProjetVueProgramme[]
  projects_not_accessible: number
  projects_not_accessible_label: string | null
  partial: boolean
  projects_failed: number
}

// ---------------------------------------------------------------------------------------------
// N28 : boîte d'attention (motif de refus avant le clic) et gouvernance du /kpi
// ---------------------------------------------------------------------------------------------

export interface ElementAttention {
  id: string
  kind: string
  who?: string
  /** Nom affiché de `who`, servi par la passerelle ; `null` pour qui n'est pas un utilisateur de l'organisation. */
  who_name?: string | null
  what: string | null
  created_at: string
  mission_id?: string | null
  /** Refus avant le clic calculé par la passerelle (`ACCESS_DENIED`, `SELF_APPROVAL_REFUSED`…), `null` si l'action est ouverte. */
  refusal_code: string | null
  workspace_id?: string
}

export interface AttentionServeur {
  elements: ElementAttention[]
  next_cursor: string | null
}

export interface GouvernanceKpi {
  governance?: { oldest_pending_approval_s?: number | null; pending?: number }
}

export const agregatsApi = {
  santeProjet: (workspaceId: string) => ouAbsent(() => request<SanteServeur>(`${ws(workspaceId)}/health`)),
  cycleProjet: (workspaceId: string) => ouAbsent(() => request<CycleServeur>(`${ws(workspaceId)}/lifecycle`)),
  demandes: (workspaceId: string) => ouAbsent(() => request<DemandesServeur>(`${ws(workspaceId)}/demands`)),
  /** Fil : 404 = introuvable ou non membre (jamais un repli). */
  demande: (demandId: number) => request<FilDemandeServeur>(`/demands/${encodeURIComponent(String(demandId))}`),
  /** O9 : ré-enfile le cadrage en échec d'une demande (409 `CADRAGE_NOT_FAILED` sinon). */
  relancerCadrage: (demandId: number) =>
    request<{ job_id: string; retries_job: string; demand_id: number; status: string }>(
      `/demands/${encodeURIComponent(String(demandId))}/cadrage/retry`, { method: 'POST' }),
  limitesEnCours: (workspaceId: string) => ouAbsent(() => request<LimitesServeur>(`${ws(workspaceId)}/wip-limits`)),
  /** Remplace les limites `{colonne: limite}` ; 403 `WIP_LIMITS_FORBIDDEN` hors propriétaire ou « backlog.wip_limits ». */
  reglerLimitesEnCours: (workspaceId: string, limites: Record<string, number>) =>
    request<unknown>(`${ws(workspaceId)}/wip-limits`, { method: 'PUT', body: JSON.stringify(limites) }),
  pretBacklog: (workspaceId: string) => ouAbsent(() => request<PretBacklogServeur>(`${ws(workspaceId)}/backlog/readiness`)),
  regressions: (workspaceId: string, campaignId?: number) =>
    ouAbsent(() => request<RegressionsServeur>(`${ws(workspaceId)}/regressions${campaignId != null ? `?campaign_id=${campaignId}` : ''}`)),
  accepterRegression: (workspaceId: string, entree: AccepterRegressionEntree) =>
    request<AcceptationRegression & { id: number; campaign_id: number; test_case_id: number }>(
      `${ws(workspaceId)}/regressions/acceptances`, { method: 'POST', body: JSON.stringify(entree) }),
  vueProgramme: (programId: string) => ouAbsent(() => request<VueProgrammeServeur>(`/programs/${encodeURIComponent(programId)}/overview`)),
  attentionProjet: (workspaceId: string) =>
    ouAbsent(() => request<AttentionServeur>(`/attention?scope=projet&workspace_id=${encodeURIComponent(workspaceId)}`)),
  gouvernanceKpi: (workspaceId: string) => ouAbsent(() => request<GouvernanceKpi>(`/kpi?workspace_id=${encodeURIComponent(workspaceId)}`)),
}
